/**
 * v4Tokens — Single source of truth for V4 canvas chrome.
 *
 * REASON THIS EXISTS
 * ──────────────────
 * Phases 5/6/7 of V4 grew organically. Each new component reached for
 * ad-hoc colors (purple gradients, lime greens, terracotta oranges)
 * and ad-hoc spacing (gap:6, gap:8, gap:12, gap:14 sprinkled at random).
 * The result: a canvas that's individually-pretty but collectively
 * scattered. The user feedback in Phase 7c was direct — "V4 looks
 * scattered and not elegantly arranged."
 *
 * This module fixes that by EXPORTING the V2 design tokens that the
 * rest of the governance platform already uses (BRAND teal, stone
 * palette S, ACCENT semantic colors, V2_PAL canvas-construct palette)
 * plus 5 small scales (SPACING / RADIUS / FONT / SHADOW / Z) so every
 * V4 component has one place to look.
 *
 * HOW TO USE
 * ──────────
 *   import { BRAND, S, ACCENT, SPACING, RADIUS, FONT, SHADOW, Z } from './v4Tokens';
 *
 *   const myButton = {
 *     padding:    `${SPACING.s}px ${SPACING.m}px`,
 *     borderRadius: RADIUS.md,
 *     fontSize:   FONT.md,
 *     color:      '#ffffff',
 *     background: BRAND.gradient,
 *   };
 *
 * Anything NOT covered by these tokens is a design smell — propose an
 * addition here before inventing a new ad-hoc value in a component file.
 */

import {
  BRAND, S, ACCENT, STATUS_MAP, btnBase, inputBase,
} from '../../components/common/GovernanceDesignSystem';
import { V2_PAL, V2_BRAND, V2_STONE, V2_ACCENT } from '../v2/nodes/v2Palette';

/* ───── Re-exports — never invent your own palette ────────────────── */
export { BRAND, S, ACCENT, STATUS_MAP, btnBase, inputBase };
export { V2_PAL, V2_BRAND, V2_STONE, V2_ACCENT };

/* ───── SPACING — 5 sizes, never anything between ─────────────────── *
 * Everything in V4 should pick from this enum. If you find yourself
 * reaching for `gap: 7`, you almost certainly want SPACING.s (6) or
 * SPACING.m (10). The 8-bucket between is intentionally absent —
 * forces a choice, prevents drift.                                     */
export const SPACING = Object.freeze({
  xs: 4,   // tight icon spacing inside a row
  s:  6,   // standard inline gap (button icon + text, chip + content)
  m:  10,  // section gap (between FieldGroups in an Inspector)
  l:  14,  // panel padding / between major rows
  xl: 20,  // page-level margins, big section breaks
});

/* ───── RADIUS — 5 sizes ──────────────────────────────────────────── *
 * sm  = chips, small badges
 * md  = buttons, inputs, small cards (DEFAULT for interactive elements)
 * lg  = panels, popovers
 * xl  = modals, hero cards
 * pill= status pills, FABs                                            */
export const RADIUS = Object.freeze({
  sm:   4,
  md:   6,
  lg:   8,
  xl:   12,
  pill: 999,
});

/* ───── FONT — 5 sizes ────────────────────────────────────────────── *
 * xs  = kbd hints, meta timestamps
 * sm  = chip labels, secondary text
 * md  = button labels, body text inside panels (DEFAULT)
 * lg  = input text (matches inputBase)
 * xl  = panel titles, node labels                                     */
export const FONT = Object.freeze({
  xs: 10,
  sm: 11,
  md: 12,
  lg: 13,
  xl: 15,
});

/* ───── FONT_WEIGHT ───────────────────────────────────────────────── */
export const WEIGHT = Object.freeze({
  regular: 400,
  medium:  500,
  semibold:600,
  bold:    700,
});

/* ───── SHADOW — 3 elevations ─────────────────────────────────────── *
 * flat    = card border-only (no shadow)
 * panel   = right-rail / inspector panel — subtle lift
 * popover = dropdowns, command palette, AI builder card — pronounced
 *           lift so it's clearly above the canvas surface              */
export const SHADOW = Object.freeze({
  flat:    'none',
  panel:   '0 1px 3px rgba(15, 23, 42, 0.08)',
  popover: '0 14px 36px -10px rgba(15, 23, 42, 0.22), 0 3px 8px -2px rgba(15, 23, 42, 0.10)',
});

/* ───── Z-INDEX — 5 layers ────────────────────────────────────────── *
 * Stops the "z=8 vs z=9 vs z=11" guessing game from Phase 6/7 where
 * each panel just picked a number bigger than the last one.            */
export const Z = Object.freeze({
  canvas:    1,
  panel:     10,   // right-rail panel, inspector
  popover:   20,   // dropdowns, ⌘K palette, runs list
  modal:     30,   // create-workflow dialog
  toast:     40,   // toast notifications (host-owned, here for reference)
});

/* ───── Semantic helpers — common patterns built from the above ──── */

/**
 * Primary-action button style. Teal brand, white text, mid font.
 * Use for Run, Save, Apply, Create, etc.
 */
export const primaryBtn = Object.freeze({
  display: 'inline-flex', alignItems: 'center', gap: SPACING.xs,
  padding: `${SPACING.s}px ${SPACING.m}px`,
  fontSize: FONT.md, fontWeight: WEIGHT.semibold,
  color: '#ffffff',
  background: BRAND.gradient,
  border: 'none', borderRadius: RADIUS.md,
  cursor: 'pointer', fontFamily: 'inherit',
  transition: 'filter 160ms ease',
});

/**
 * Secondary-action button — outline style. Slate, no fill.
 * Use for Cancel, Discard, secondary actions.
 */
export const secondaryBtn = Object.freeze({
  display: 'inline-flex', alignItems: 'center', gap: SPACING.xs,
  padding: `${SPACING.s}px ${SPACING.m}px`,
  fontSize: FONT.md, fontWeight: WEIGHT.medium,
  color: S[700],
  background: '#ffffff',
  border: `1px solid ${S[200]}`, borderRadius: RADIUS.md,
  cursor: 'pointer', fontFamily: 'inherit',
});

/**
 * Ghost button — text-only, hover background. Use for icon-only
 * actions inside panels (close X, refresh, etc.).
 */
export const ghostBtn = Object.freeze({
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  padding: SPACING.xs,
  fontSize: FONT.md,
  color: S[600],
  background: 'transparent',
  border: 'none', borderRadius: RADIUS.sm,
  cursor: 'pointer', fontFamily: 'inherit',
});

/**
 * Input style for text/number/textarea. Matches inputBase from
 * GovernanceDesignSystem but bound to V4 tokens.
 */
export const v4Input = Object.freeze({
  width: '100%',
  padding: `${SPACING.xs + 2}px ${SPACING.s + 2}px`,  // 6/8
  fontSize: FONT.md, color: S[900],
  background: '#ffffff',
  border: `1px solid ${S[200]}`,
  borderRadius: RADIUS.md,
  outline: 'none', fontFamily: 'inherit',
  transition: 'border-color 0.15s, box-shadow 0.15s',
});

/**
 * Severity tone — semantic mapping for validation issues + run statuses.
 * Use this instead of inventing ad-hoc reds/ambers/blues per component.
 */
export const SEVERITY = Object.freeze({
  error:   { ...ACCENT.rose,    label: 'Error' },
  warning: { ...ACCENT.amber,   label: 'Warning' },
  info:    { ...ACCENT.sky,     label: 'Info' },
  success: { ...ACCENT.emerald, label: 'OK' },
});

/**
 * Run-status tone — maps backend run statuses to ACCENT palette.
 * Centralizes the duplicated maps in V4RunsDropdown + V4RunDetailPanel.
 */
export const RUN_STATUS = Object.freeze({
  COMPLETED:             { ...ACCENT.emerald, label: 'Completed' },
  COMPLETED_WITH_ERRORS: { ...ACCENT.amber,   label: 'Partial' },
  RUNNING:               { ...ACCENT.sky,     label: 'Running' },
  PAUSED:                { ...ACCENT.slate,   label: 'Paused' },
  PENDING_APPROVAL:      { ...ACCENT.violet,  label: 'Awaiting approval' },
  PENDING:               { ...ACCENT.slate,   label: 'Pending' },
  SKIPPED:               { ...ACCENT.slate,   label: 'Skipped' },
  FAILED:                { ...ACCENT.rose,    label: 'Failed' },
  TERMINATED:            { ...ACCENT.rose,    label: 'Terminated' },
  TIMED_OUT:             { ...ACCENT.rose,    label: 'Timed out' },
});

/**
 * kbd-chip style for inline keyboard hints in tooltips and footers.
 */
export const kbdChip = Object.freeze({
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  padding: '1px 5px',
  fontSize: FONT.xs, fontWeight: WEIGHT.semibold,
  color: S[600], background: S[200],
  border: 'none', borderRadius: RADIUS.sm,
  fontFamily: 'inherit', lineHeight: 1.4,
});

/**
 * Status pill — small uppercase chip used for run/workflow status.
 * Pass an entry from SEVERITY or RUN_STATUS.
 */
export function statusPillStyle(tone) {
  return {
    display: 'inline-flex', alignItems: 'center', gap: SPACING.xs,
    padding: '2px 7px',
    fontSize: FONT.xs, fontWeight: WEIGHT.bold,
    color: tone.text, background: tone.bg,
    border: 'none', borderRadius: RADIUS.pill,
    textTransform: 'uppercase', letterSpacing: 0.3,
  };
}
